import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsLayout } from '../../components/SettingsLayout';
import { ArrowBack, CheckCircle, Block, AddCard, ArrowForward } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { CREDIT_PACK_PRESETS, creditsToImages } from '../../lib/utils';

export const PurchaseCreditsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [selectedPack, setSelectedPack] = useState<'basic' | 'growth' | 'scale'>('growth');

  const packLabels = {
    basic: t('pricing.packBasic'),
    growth: t('pricing.packGrowth'),
    scale: t('pricing.packScale'),
  } as const;
  const selectedPackPrice = CREDIT_PACK_PRESETS.find((pack) => pack.id === selectedPack)?.priceUsd ?? 0;

  const handlePurchase = () => {
    alert(t('purchaseCredits.processing'));
    setTimeout(() => {
      alert(t('purchaseCredits.success'));
      navigate('/settings/billing');
    }, 1000);
  };

  return (
    <SettingsLayout>
      <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="mb-10 flex items-center gap-4">
          <button
            onClick={() => navigate('/settings/billing')}
            className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-900 hover:border-slate-300 transition-colors bg-white"
          >
            <ArrowBack />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{t('purchaseCredits.title')}</h1>
            <p className="text-slate-500 mt-1 text-base">{t('purchaseCredits.subtitle')}</p>
            <p className="text-xs text-slate-400 mt-2">{t('purchaseCredits.priceHint')}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
              {CREDIT_PACK_PRESETS.map((pack) => {
                const isSelected = selectedPack === pack.id;
                const imageCount = creditsToImages(pack.credits);
                const showPriority = pack.id === 'scale';
                const showBatch = pack.id !== 'basic';
                const features = [
                  { label: t('purchaseCredits.featureNeverExpire'), available: true },
                  showBatch
                    ? { label: t('purchaseCredits.featureBatch'), available: true }
                    : { label: t('purchaseCredits.featureNoBatch'), available: false },
                  ...(showPriority
                    ? [{ label: t('purchaseCredits.featurePriority'), available: true }]
                    : []),
                ];

                return (
                  <div
                    key={pack.id}
                    onClick={() => setSelectedPack(pack.id)}
                    className={`relative group cursor-pointer border rounded-2xl p-6 transition-all ${
                      isSelected
                        ? 'border-primary bg-blue-50/10 ring-2 ring-primary/20'
                        : 'border-slate-200 hover:border-blue-200 bg-white'
                    }`}
                  >
                    {pack.highlight && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-sm">
                        {t('purchaseCredits.bestValue')}
                      </div>
                    )}
                    <div className="mb-4">
                      <h4 className={`text-sm font-bold uppercase tracking-widest ${pack.highlight ? 'text-blue-600' : 'text-slate-500'}`}>
                        {packLabels[pack.id]}
                      </h4>
                      <div className="mt-2 flex items-baseline">
                        <span className="text-3xl font-bold text-slate-900">{pack.credits}</span>
                        <span className="ml-1 text-slate-500 text-sm font-medium">{t('purchaseCredits.creditsLabel')}</span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1 font-medium">
                        {t('purchaseCredits.imagesLabel')} {imageCount} {t('purchaseCredits.imagesUnit')}
                      </p>
                      <div className="mt-2">
                        <span className="text-xl font-bold text-slate-900">${pack.priceUsd}</span>
                        {pack.bonusPercent > 0 && (
                          <span className="ml-1 text-xs text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded">
                            {t('purchaseCredits.saveLabel')} {pack.bonusPercent}%
                          </span>
                        )}
                      </div>
                    </div>
                    <ul className="space-y-3 mb-2">
                      {features.map((feature, index) => (
                        <li
                          key={`${pack.id}-${index}`}
                          className={`flex items-center gap-2 text-sm ${feature.available ? 'text-slate-600' : 'text-slate-400'}`}
                        >
                          {feature.available ? (
                            <CheckCircle className="text-emerald-500" style={{ fontSize: 18 }} />
                          ) : (
                            <Block style={{ fontSize: 18 }} />
                          )}
                          <span className={feature.available ? '' : 'line-through'}>{feature.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            <div className="space-y-6">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest border-b border-slate-100 pb-4">
                {t('purchaseCredits.paymentTitle')}
              </h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between p-4 border-2 border-primary bg-blue-50/30 rounded-xl cursor-pointer shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-6 bg-slate-900 rounded flex items-center justify-center text-[10px] text-white font-bold italic">VISA</div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{t('purchaseCredits.visaLabel')}</p>
                      <p className="text-xs text-slate-500">{t('purchaseCredits.visaExpire')}</p>
                    </div>
                  </div>
                  <input defaultChecked className="w-4 h-4 text-primary border-slate-300 focus:ring-primary" name="payment" type="radio" />
                </label>
                <label className="flex items-center justify-between p-4 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#07C160]/10 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#07C160]" fill="currentColor" viewBox="0 0 24 24"><path d="M8.28,7.31C9.64,7.31,10.74,8.19,10.74,9.27c0,1.08-1.1,1.96-2.46,1.96C6.91,11.23,5.81,10.35,5.81,9.27C5.81,8.19,6.91,7.31,8.28,7.31z M15.72,7.31c1.36,0,2.46,0.88,2.46,1.96c0,1.08-1.1,1.96-2.46,1.96s-2.46-0.88-2.46-1.96C13.26,8.19,14.36,7.31,15.72,7.31z M12.5,2C6.98,2,2.5,5.58,2.5,10c0,2.44,1.38,4.61,3.53,6.03c-0.2,0.61-0.71,2.02-0.71,2.02l2.36-1.18c0.8,0.22,1.65,0.34,2.54,0.34c0.2,0,0.39-0.01,0.59-0.03c-0.2-0.41-0.31-0.87-0.31-1.35c0-3.31,3.13-6,7-6c0.55,0,1.09,0.05,1.6,0.16C18.66,6.38,15.89,2,12.5,2z M17,11c-3.31,0-6,2.24-6,5s2.69,5,6,5c0.75,0,1.46-0.12,2.11-0.33l1.89,0.94c0,0-0.41-1.13-0.57-1.62c1.55-0.99,2.57-2.61,2.57-4.44C23,13.24,20.31,11,17,11z M14.75,14.5c0.69,0,1.25,0.45,1.25,1s-0.56,1-1.25,1s-1.25-0.45-1.25-1S14.06,14.5,14.75,14.5z M19.25,14.5c0.69,0,1.25,0.45,1.25,1s-0.56,1-1.25,1s-1.25-0.45-1.25-1S18.56,14.5,19.25,14.5z"></path></svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{t('purchaseCredits.wechat')}</p>
                      <p className="text-xs text-slate-500">{t('purchaseCredits.wechatDesc')}</p>
                    </div>
                  </div>
                  <input className="w-4 h-4 text-primary border-slate-300 focus:ring-primary" name="payment" type="radio" />
                </label>
                <label className="flex items-center justify-between p-4 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#1677FF]/10 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#1677FF]" fill="currentColor" viewBox="0 0 24 24"><path d="M21.272 0H2.728C1.22 0 0 1.221 0 2.728v18.544C0 22.779 1.221 24 2.728 24h18.544C22.779 24 24 22.779 24 21.272V2.728C24 1.221 22.779 0 21.272 0zM17.1 11.235h-3.32c-.321-1.107-.738-2.227-1.341-3.218h4.661v-1.89h-5.91c-.482-1.077-1.144-2.138-2.025-2.915H15.11V1.304H5.972v1.908h2.32c.691.562 1.271 1.396 1.706 2.385H5.857v1.89h4.646c.54.891.905 1.838 1.157 2.82H6.947v1.87h5.176c-.053 2.185-.754 4.095-1.928 5.484-1.22-1.127-2.126-2.658-2.731-4.148H5.43c.69 2.176 1.94 4.316 3.75 6.012l-2.08 1.83 1.298 1.298 2.016-1.782c1.783 1.095 4.026 1.71 6.574 1.71.21 0 .405-.015.6-.015.195-.015.39-.015.585-.03.18-.015.36-.03.54-.045.18-.015.345-.03.51-.06.165-.015.315-.045.465-.06.15-.015.285-.045.435-.06a4.887 4.887 0 00.39-.075c.135-.03.255-.06.39-.09a2.31 2.31 0 00.345-.105c.12-.045.24-.075.345-.12.105-.045.21-.075.315-.12.09-.045.18-.09.27-.135.09-.045.165-.105.255-.15.075-.045.15-.09.21-.15.06-.045.135-.105.195-.15s.105-.09.165-.135a1.056 1.056 0 00.15-.135c.045-.045.105-.105.15-.15.045-.045.075-.09.12-.135.045-.045.075-.09.105-.15.03-.045.06-.09.09-.135.03-.045.06-.09.075-.135a1.29 1.29 0 00.075-.135c.015-.045.03-.09.045-.135.015-.045.03-.09.045-.135v-6.3h-4.215zm-.93 4.43c-1.396 0-2.657-.315-3.693-.84.975-1.11 1.576-2.4 1.756-3.81h2.237v4.65h-.3z"></path></svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{t('purchaseCredits.alipay')}</p>
                      <p className="text-xs text-slate-500">{t('purchaseCredits.alipayDesc')}</p>
                    </div>
                  </div>
                  <input className="w-4 h-4 text-primary border-slate-300 focus:ring-primary" name="payment" type="radio" />
                </label>
                <label className="flex items-center justify-between p-4 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <AddCard className="text-slate-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{t('purchaseCredits.addNew')}</p>
                      <p className="text-xs text-slate-500">{t('purchaseCredits.addNewDesc')}</p>
                    </div>
                  </div>
                  <input className="w-4 h-4 text-primary border-slate-300 focus:ring-primary" name="payment" type="radio" />
                </label>
              </div>
              <div className="flex items-center gap-4 pt-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('purchaseCredits.acceptedMethods')}</span>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-5 bg-slate-100 rounded text-[8px] flex items-center justify-center font-bold text-slate-400 border border-slate-200">VISA</div>
                  <div className="w-8 h-5 bg-slate-100 rounded text-[8px] flex items-center justify-center font-bold text-slate-400 border border-slate-200">MC</div>
                  <div className="w-8 h-5 bg-slate-100 rounded flex items-center justify-center border border-slate-200">
                    <svg className="w-4 h-4 text-[#07C160]" fill="currentColor" viewBox="0 0 24 24"><path d="M12.5,2C6.98,2,2.5,5.58,2.5,10c0,2.44,1.38,4.61,3.53,6.03c-0.2,0.61-0.71,2.02-0.71,2.02l2.36-1.18c0.8,0.22,1.65,0.34,2.54,0.34c0.2,0,0.39-0.01,0.59-0.03c-0.2-0.41-0.31-0.87-0.31-1.35c0-3.31,3.13-6,7-6c0.55,0,1.09,0.05,1.6,0.16C18.66,6.38,15.89,2,12.5,2z"></path></svg>
                  </div>
                  <div className="w-8 h-5 bg-slate-100 rounded flex items-center justify-center border border-slate-200">
                    <svg className="w-4 h-4 text-[#1677FF]" fill="currentColor" viewBox="0 0 24 24"><path d="M21.272 0H2.728C1.22 0 0 1.221 0 2.728v18.544C0 22.779 1.221 24 2.728 24h18.544C22.779 24 24 22.779 24 21.272V2.728C24 1.221 22.779 0 21.272 0zM17.1 11.235h-3.32"></path></svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-slate-50 px-8 py-6 border-t border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-slate-500">
              <CheckCircle style={{ fontSize: 18 }} />
              <p className="text-xs font-medium">{t('purchaseCredits.security')}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('purchaseCredits.total')}</p>
                <p className="text-xl font-bold text-slate-900">
                  ${selectedPackPrice.toFixed(2)}
                </p>
              </div>
              <button
                onClick={handlePurchase}
                className="px-8 py-3 bg-primary text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 flex items-center gap-2 active:scale-95"
              >
                {t('purchaseCredits.purchaseButton')}
                <ArrowForward style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>
        </div>
        <footer className="mt-12 text-center pb-12">
          <p className="text-slate-400 text-xs font-medium uppercase tracking-[0.1em]">{t('purchaseCredits.footer')}</p>
        </footer>
      </div>
    </SettingsLayout>
  );
};
